import type { CartItemPut, CartMeta, CartOverview } from '~/utils/easyApi'
import type { Supplier } from '~/types'

const usePrivateCart = defineStore('private-cart', () => {
  const userStore = useUserStore()
  const { $easyApi } = useNuxtApp()
  const { withAuthRetry } = useWithAuthRetry()

  const customerHandle = computed(() => userStore?.customerHandle ?? undefined)
  const data = ref<CartOverview | null>(null)
  const isPrivateCartLoading = ref(true)
  let requestId = 0

  // Do not use useFetch/useAsyncData. On a hard reload the layout creates this
  // store during SSR, before the browser can show the page. $easyApi is a real
  // HTTP client, so that server render waits on itself and the cart (and every
  // page that shares the layout, including welcome) never finishes.
  async function refresh() {
    const token = userStore.accessToken
    const customer = customerHandle.value
    if (!token || !customer) {
      if (!userStore.isAuthPending)
        isPrivateCartLoading.value = false
      return
    }
    const id = ++requestId
    isPrivateCartLoading.value = true
    try {
      const next = await withAuthRetry(() => $easyApi<CartOverview>('/cart', {
        query: { customer },
      }))
      if (id === requestId)
        data.value = next
    }
    catch {
      if (id === requestId)
        data.value = null
    }
    finally {
      if (id === requestId)
        isPrivateCartLoading.value = false
    }
  }

  if (import.meta.client) {
    watch([customerHandle, () => userStore.accessToken], () => {
      void refresh()
    }, { immediate: true })
  }

  function $reset() {
    isPrivateCartLoading.value = false
    data.value = null
    void refresh()
  }

  return {
    data,
    isPrivateCartLoading,
    $reset,
  }
})

export const useCart = defineStore('cart', () => {
  const log = useLogger('useCart')
  const { $easyApi } = useNuxtApp()
  const { withAuthRetry } = useWithAuthRetry()
  const userStore = useUserStore()
  const featureSettingsStore = useFeatureSettingsStore()
  const privateCart = usePrivateCart()
  const cartsLoadingState = ref<Record<string, boolean>>({})
  const supplierStore = useSupplier()
  const { getItemPrice } = usePriceUtils()
  const { track: trackProductFruits, setFirstOrderCompleted } = useProductFruits()

  const isLoading = ref(false)
  const selectedSupplier = ref<string | null>(null)
  const selectedProduct = ref<string | null>(null)

  // Hide carts for suppliers blocked by the active GPO.
  // TODO: this filtering will be done in the backend when the API is moved to base
  const blockedSupplierHandles = computed(() => {
    const gpoHandle = unref(userStore.gpoHandle)
    return featureSettingsStore.settings?.clinicChain?.[gpoHandle]?.exclude?.suppliers ?? []
  })

  const combinedSuppliersCount = computed(() => privateCart.data?.combinedSuppliers.flat().length ?? 0)
  const carts = computed(() => {
    return (privateCart.data?.supplierCarts ?? [])
      .filter(cart => cart.items.length > 0 && !blockedSupplierHandles.value.includes(cart.supplier))
      .map((cart) => {
        const isCombined = privateCart.data?.combinedSuppliers.flat().includes(cart.supplier)
        const supplier = supplierStore.suppliers.find(supplier => supplier.handle === cart.supplier)
        const sortedItems = [...cart.items].sort((a, b) =>
          a.handle.localeCompare(b.handle) || a.reference.localeCompare(b.reference),
        )

        const totalSum = cart.items.reduce((total, item) => {
          const itemPrice = getItemPrice(item)
          const itemQuantity = item.quantity || 0
          return total + itemPrice * itemQuantity
        }, 0)

        return {
          ...cart,
          supplier: supplier ?? {} as Partial<Supplier>,
          totalSum,
          isCombined,
          items: sortedItems,
        }
      })
      .sort((a, b) => Number(b.isCombined) - Number(a.isCombined))
  })
  const totalAmount = computed(() =>
    carts.value.reduce((acc, cart) => acc + cart.items.reduce((sum, item) => sum + item.quantity, 0), 0),
  )

  async function updateCartItem(supplierHandle: string, item: CartItemPut, updateState: boolean = true) {
    if (!userStore.data?.handle) return
    const customer = userStore.data.handle
    const saveState = privateCart.data ? { ...privateCart.data } : null
    cartsLoadingState.value[supplierHandle] = true
    try {
      privateCart.data = {
        combinedSuppliers: saveState?.combinedSuppliers ?? [],
        supplierCarts: (saveState?.supplierCarts ?? []),
      }
      if (updateState) {
        privateCart.data = await withAuthRetry(() => $easyApi<CartOverview>(`/cart/${supplierHandle}/items`, {
          method: 'PUT',
          query: { customer },
          body: item,
        }))
      }
      else {
        await withAuthRetry(() => $easyApi<CartOverview>(`/cart/${supplierHandle}/items`, {
          method: 'PUT',
          query: { customer },
          body: item,
        }))
      }
    }
    catch (error) {
      privateCart.data = saveState
      throw error
    }
    finally {
      isLoading.value = false
      cartsLoadingState.value[supplierHandle] = false
    }
  }

  async function updateCartMeta(supplierHandle: string, metaInfo: CartMeta) {
    if (!userStore.data) {
      return false
    }

    const customer = userStore.data.handle
    isLoading.value = true
    try {
      privateCart.data = await withAuthRetry(() => $easyApi<CartOverview>(`/cart/${supplierHandle}/meta`, {
        method: 'PUT',
        query: { customer },
        body: metaInfo,
      }))
      return true
    }
    catch (error) {
      log.warn('Failed to update cart meta', { error })
      return false
    }
    finally {
      isLoading.value = false
    }
  }

  async function mergeCartItemByReference(supplierHandle: string, itemHandle: string, newReference: string, oldReference: string) {
    cartsLoadingState.value[supplierHandle] = true
    try {
      const currentQuantity = carts.value
        .flatMap(cart => cart.items)
        .filter(item => item.handle === itemHandle)
        .reduce((sum, item) => sum + item.quantity, 0)

      await updateCartItem(supplierHandle, {
        handle: itemHandle,
        quantity: 0,
        reference: oldReference.toUpperCase() as 'OFFICE' | 'PHARMACY',
      }, false)

      await updateCartItem(supplierHandle, {
        handle: itemHandle,
        quantity: currentQuantity,
        reference: newReference.toUpperCase() as 'OFFICE' | 'PHARMACY',
      })
    }
    catch (error) {
      log.warn('Error occurred while toggling the customer-reference', { error })
    }
    finally {
      cartsLoadingState.value[supplierHandle] = false
      isLoading.value = false
    }
  }

  async function submitOrder(supplierHandles: string[]) {
    if (!userStore.data?.handle) {
      log.warn('Customer handle not found!')
      return null
    }
    const customer = userStore.data.handle
    const orderStore = useOrderHistoryStore()
    isLoading.value = true
    try {
      const { masterOrderHandle, combinedSuppliers, supplierCarts } = await withAuthRetry(() => $easyApi<CartOverview>('/cart', {
        method: 'POST',
        query: { customer },
        body: supplierHandles,
      }))
      if (masterOrderHandle) {
        privateCart.data = {
          combinedSuppliers,
          supplierCarts,
        }

        orderStore.$reset()
        orderStore.hasAnyOrders = true
        orderStore.ensureListLoaded()
        // $reset cleared last-ordered products; refetch so the welcome page picks
        // up the new order, both in the table and in the order recency.
        void orderStore.reloadFrequentOrders()
        setFirstOrderCompleted(true)
        trackProductFruits('orderPlaced')
        return { masterOrderHandle }
      }
      else {
        log.warn('Order submitted but masterOrderHandle is missing')
        return null
      }
    }
    catch (error: any) {
      log.warn('Error submitting order:', { error })
      return { errors: { unknown: true } }
    }
    finally {
      isLoading.value = false
    }
  }

  function setSelectedSupplier(handle: string | null) {
    selectedSupplier.value = handle
  }

  function setSelectedProduct(handle: string | null) {
    selectedProduct.value = handle
  }

  const isCartLoading = (supplierHandle: string) => {
    return cartsLoadingState.value[supplierHandle] || false
  }

  const totalLoading = computed(() => isLoading.value || privateCart.isPrivateCartLoading)

  function $reset() {
    cartsLoadingState.value = {}
    isLoading.value = false
    privateCart.$reset()
  }

  return {
    carts,
    totalAmount,
    combinedSuppliersCount,
    updateCartItem,
    updateCartMeta,
    mergeCartItemByReference,
    submitOrder,
    isLoading: totalLoading,
    cartsLoadingState,
    isCartLoading,
    setSelectedSupplier,
    selectedSupplier,
    selectedProduct,
    setSelectedProduct,
    $reset,
  }
})
